/** "Dòng thời gian": the latest events of a domain (Tổng quan › Hoạt động gần đây). */

import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, type DomainDetail } from '../../api';
import { Section } from '../../components/blocks';
import { formatDateTime } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { domainTabUrl } from './tabs/moved';

/** "Dòng thời gian": the latest events of the domain, with the full history one click away. */
export function DomainTimelinePanel({ detail }: { detail: DomainDetail }) {
  const { t: tu } = useTranslation('ux');
  const L = useLabels();
  const d = detail.domain;
  const r = useApi((signal) => api.getDomainTimeline(d.id, { limit: 6 }, { signal }), [d.id]);
  const events = r.status === 'success' ? [...r.data.events].sort((a, b) => b.sequence - a.sequence).slice(0, 6) : [];
  return (
    <Section
      title={tu('domain.timeline.title')}
      actions={
        <Button asChild variant="outline" size="sm">
          <Link to={domainTabUrl(d.ascii_name, 'history')}>
            {tu('domain.timeline.all')} <ArrowRight />
          </Link>
        </Button>
      }
    >
      {r.status === 'loading' ? <p className="text-sm text-muted-foreground">…</p> : null}
      {r.status === 'error' ? <p className="text-sm text-muted-foreground">{tu('domain.timeline.failed')}</p> : null}
      {r.status === 'success' && !events.length ? <p className="text-sm text-muted-foreground">{tu('domain.timeline.none')}</p> : null}
      <ol className="relative space-y-4 border-l border-border pl-4">
        {events.map((e) => (
          <li key={e.id} className="relative">
            <span aria-hidden="true" className="absolute top-1.5 -left-[21px] size-2.5 rounded-full border-2 border-card bg-primary" />
            <Link to={`${domainTabUrl(d.ascii_name, 'history')}#${encodeURIComponent(e.id)}`} className="text-[13px] font-semibold hover:underline">
              {L.eventType(e.event_type)}
            </Link>
            <div className="text-xs text-muted-foreground">
              {formatDateTime(e.occurred_at)}
              {e.actor_name ? ` · ${e.actor_name}` : ''}
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}
